import React, { createContext, useReducer, PropsWithChildren } from "react";
import { useLocalStorage } from "@services/common/hooks/useLocalStorage";
import { useSpecificListPreferences } from "@common/components/SpecificPreferences/hooks";
import { sortColumnOrder } from "@common/components/SpecificPreferences/utils";

type Actions = Partial<Omit<IColumnsFilterContext, "dispatch">>;

export type ColDefWithTab<T, O = undefined> = ColDef<T, O> & {
  tab?: TabValue[];
  disabledToHide?: boolean;
  default?: boolean;
  alwaysExportable?: boolean;
};

export enum TabValue {
  Info = 0,
  MapView = 2,
  Archive = 3,
  Export = 99,
}

export type TabDefinitionKey = "Info" | "MapView" | "Archive";
export type TabDefinition = {
  label: string;
  value: TabValue;
};

export const tabDefinition: Record<TabDefinitionKey, TabDefinition> = {
  MapView: { label: "Map View", value: TabValue.MapView },
  Info: { label: "Info", value: TabValue.Info },
  Archive: { label: "Archive", value: TabValue.Archive },
};

export type TabName =
  | "Accounts"
  | "Countries"
  | "Devices"
  | "Rooms"
  | "Locations"
  | "Users"
  | "Tickets"
  | "Meetings"
  | "Workflows"
  | "Triggers";

interface IColumnsFilterContext {
  dispatch: (action: Actions) => void;
  tabValue: TabValue;
  columnsList?: { label: string; key: string }[];
  activeColumnsList?: string[];
  defaultColumnsList?: string[];
  disabledForHideColumnsList?: string[];
  tabName?: TabName;
}
export type GetColumnsFunctionType<T, O = undefined> = () => ColDefWithTab<
  T,
  O
>[];
export const FilterColumnContext = createContext<IColumnsFilterContext>({
  dispatch: () => {},
  tabValue: 0,
});

type FilterColumnProviderProps<T, O> = PropsWithChildren<{
  getColumnFunction: GetColumnsFunctionType<T, O>;
  tabName: TabName;
  tabValue?: TabValue;
}>;

const getColumns = <T, O = undefined>(
  columns: ColDefWithTab<T, O>[],
  tabValue: TabValue
) =>
  columns
    .filter((c) => c.tab === undefined || c.tab.indexOf(tabValue) !== -1)
    .map(({ key, label }) => ({ key: key as string, label }));

const getDefaultColumns = <T, O = undefined>(
  columns: ColDefWithTab<T, O>[],
  tabValue: TabValue
): string[] =>
  columns
    .filter((c) => c.tab === undefined || c.tab.indexOf(tabValue) !== -1)
    .filter((c) => c.default)
    .map((c) => c.key as string);

export const addAlwaysExportableColumn = <T,>(
  activeColumn: ColDefWithTab<T>[],
  currentColumns: ColDefWithTab<T>[]
) => {
  const alwaysExportableColumn = activeColumn.find((x) => x.alwaysExportable);
  if (alwaysExportableColumn) {
    return [
      alwaysExportableColumn,
      ...currentColumns.filter((x) => x.key !== alwaysExportableColumn.key),
    ];
  }
  return activeColumn;
};

export const FilterColumnsProvider = <T, O>(
  props: FilterColumnProviderProps<T, O>
) => {
  const { getColumnFunction, tabName, tabValue } = props;
  const key = `${tabName}_active_columns`;
  const [activeColumnsState, saveActiveColumnsState] =
    useLocalStorage<string[]>(key);
  const [{ columnOrder }] = useSpecificListPreferences();
  const columnsData = getColumnFunction();
  const columns = columnsData.filter((x) => x.label);
  const disabledForHideColumnNames = columnsData.filter(
    (x) => x.disabledToHide
  );

  const getActiveColumns = (tabValue?: TabValue): string[] => {
    const activeColumn =
      columnOrder.length > 0 ? columnOrder : activeColumnsState;
    return tabValue !== undefined && [TabValue.Info].includes(tabValue)
      ? activeColumn ??
          columnsData?.filter((x) => x.default).map((x) => x.key as string)
      : columnsData?.map((x) => x.key as string);
  };

  const [state, dispatch] = useReducer(
    (state: IColumnsFilterContext, action: Actions) => {
      switch (action.tabValue) {
        case TabValue.Archive: {
          const columnList = getColumns(columns, action.tabValue);
          return {
            ...state,
            activeColumnsList: columnList.map((x) => x.key),
            columnsList: columnList,
            tabValue: action.tabValue,
          };
        }
        case TabValue.Info: {
          const columnList = sortColumnOrder(
            getColumns(columns, action.tabValue),
            columnOrder
          );
          return {
            ...state,
            activeColumnsList: getActiveColumns(action.tabValue),
            defaultColumnsList: getDefaultColumns(columnsData, action.tabValue),
            columnsList: columnList,
            tabValue: action.tabValue,
          };
        }

        default:
          saveActiveColumnsState(action.activeColumnsList as string[]);
          return {
            ...state,
            ...action,
          };
      }
    },
    {
      dispatch: () => {},
      activeColumnsList: getActiveColumns(props.tabValue),
      columnsList: getColumns(columns, props.tabValue ?? TabValue.Info),
      tabValue: props.tabValue ?? TabValue.Info,
      disabledForHideColumnsList: disabledForHideColumnNames.map(
        (c) => c.key as string
      ),
      defaultColumnsList: getDefaultColumns(
        columnsData,
        tabValue ?? TabValue.Info
      ),
      tabName: props.tabName,
    }
  );

  return (
    <FilterColumnContext.Provider value={{ ...state, dispatch }}>
      {props.children}
    </FilterColumnContext.Provider>
  );
};
